import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Container, Grid } from "@mui/material";
import type ApplicationRoles from "constants/ApplicationRoles";
import type Statut from "constants/Statut";
import { dateUtil, Authorization } from "@sdeapps/react-core";
import type { User } from "@sdeapps/react-core";
import graphService from "services/graphService";
import commandesService from "services/commandesService";
import type Article from "models/Article";
import articlesService from "services/articlesService";
import PageTitle from "components/molecules/PageTitle";
import ArticleFilters from "components/molecules/ArticleFilters";
import TriByArticleList from "./TriByArticleList";
import type CommandeArticleTri from "models/CommandeArticleTri";

function isSameCommandeArticle(a: CommandeArticleTri, b: CommandeArticleTri): boolean {
  return (
    a.articleId === b.articleId &&
    a.commandeId === b.commandeId &&
    a.commandeArticleNumber === b.commandeArticleNumber
  );
}

type FilterCommandesPageProps = {
  availableStatuts: Array<Statut>;
  defaultStatuts?: Array<Statut>;
  roles: ApplicationRoles | Array<ApplicationRoles>;
  pageTitle?: string;
};

function FilterArticlesPage({
  availableStatuts,
  defaultStatuts,
  roles,
  pageTitle = "",
}: Readonly<FilterCommandesPageProps>): ReactNode {
  const [catalogArticles, setCatalogArticles] = useState<Array<Article>>([]);
  const [agents, setAgents] = useState<Array<User>>([]);
  const [allCommandArticles, setAllCommandArticles] = useState<Array<CommandeArticleTri>>([]);
  const [filteredArticles, setFilteredArticles] = useState<Array<CommandeArticleTri>>([]);

  const [isFetchingAndFiltering, setIsFetchingAndFiltering] = useState(true);

  async function getAllBetterCommandArticles(): Promise<Array<CommandeArticleTri>> {
    const allCommandes = await commandesService.getAll();
    allCommandes.sort((a, b) => dateUtil.compareAsc(a.dateCommande, b.dateCommande));

    const allBetterCommandesArticles: Array<CommandeArticleTri> = [];
    allCommandes.forEach((commande) => {
      commande.articles
        .filter((article) =>
          availableStatuts.map((c) => c.toLowerCase()).includes(article.statutArticle.toLowerCase())
        )
        .forEach((article) => {
          allBetterCommandesArticles.push({
            ...article,
            magasinDeRetrait: commande.magasinDeRetrait,
          });
        });
    });

    return allBetterCommandesArticles;
  }

  useEffect(() => {
    // TODO à remplacer par un (plusieurs) useErrorHandler()
    setIsFetchingAndFiltering(true);
    async function getData(): Promise<void> {
      try {
        const [_allArticles, _betterCommandesArticles] = await Promise.all([
          articlesService.getAll(),
          getAllBetterCommandArticles(),
        ]);
        _allArticles.sort((p1, p2) => p1.label.localeCompare(p2.label));

        setCatalogArticles(_allArticles);
        setAllCommandArticles(_betterCommandesArticles);

        const _agents = await graphService.getAgentsByIds(
          _betterCommandesArticles.map((a) => a.demandeurId)
        );
        setAgents(_agents);
      } catch {
        // TODO error handling
        setCatalogArticles([]);
        setAllCommandArticles([]);
        setAgents([]);
      }
      setIsFetchingAndFiltering(false);
    }

    void getData();
    // TODO: revoir les dépendances lors d'une mise à jour des règles ESLint
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Authorization roles={roles}>
      <PageTitle title={pageTitle} />
      <Container maxWidth={false}>
        <Grid container spacing={2} size={12} sx={{ alignItems: "center" }}>
          <Grid size={12}>
            <ArticleFilters
              betterArticles={allCommandArticles}
              setBetterArticles={setFilteredArticles}
              articleFilter
              tailleFilter
              statutFilter
              centreRetraitFilter
              numeroBonDeCommandeFilter
              availableArticles={catalogArticles}
              availableStatuts={availableStatuts}
              selectedStatutsDefault={defaultStatuts}
            />
          </Grid>
          <Grid size={12}>
            <TriByArticleList
              articles={filteredArticles}
              agents={agents}
              isLoading={isFetchingAndFiltering}
              onNumeroBonDeCommandeChange={(changedArticle) => {
                setAllCommandArticles(
                  allCommandArticles.map((article) =>
                    isSameCommandeArticle(article, changedArticle) ? changedArticle : article
                  )
                );
              }}
            />
          </Grid>
        </Grid>
      </Container>
    </Authorization>
  );
}

export default FilterArticlesPage;
